import { Injectable, OnDestroy } from '@angular/core';
import { AngularFireDatabase } from '@angular/fire/compat/database';
import { NavigationEnd, Router } from '@angular/router';
import { BehaviorSubject, Observable, Subscription } from 'rxjs';
import { filter, map } from 'rxjs/operators';

import { Channel } from 'ideta-library/lib/common/bot';

export type AppPage = 'login' | 'pricing' | 'create' | 'campaigns' | 'welcome';

export interface RouterEvent {
  location: AppPage;
  botId?: string;
  channel?: Channel;
}

@Injectable({
  providedIn: 'root'
})
export class CoreSessionService implements OnDestroy {
  private _routerEvent$: BehaviorSubject<RouterEvent>;
  private routerSub: Subscription;

  get routerEvent$() {
    return this._routerEvent$.pipe(filter(event => !!event));
  }

  get location(): AppPage {
    return this._routerEvent$.value.location;
  }

  constructor(
    private db: AngularFireDatabase,
    private router: Router
  ) {
    this._routerEvent$ = new BehaviorSubject(null);
    this.routerSub = this.router.events
      .pipe(
        filter(event => event instanceof NavigationEnd),
        map((event: NavigationEnd) => event.url),
        filter(url => url !== '/')
      )
      .subscribe(activeUrl => {
        const route = activeUrl.split(/\/([\\w-]+)\/?/g);
        const routerEvent: RouterEvent = {
          location: route[1] as AppPage,
          channel: (route[2] || '').split(/\??channel=([\\w]+)&?/g)[1] as Channel
        };
        this._routerEvent$.next(routerEvent);
      });
  }

  endSession() {
    this._routerEvent$.next({ location: 'login' });
  }

  ngOnDestroy(): void {
    if (this.routerSub) this.routerSub.unsubscribe();
  }

  public emit(state: RouterEvent) {
    this._routerEvent$.next(state);
  }

  public getVersion(): Observable<string> {
    return this.db.object<string>('version').valueChanges();
  }
}
